import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar, ChevronLeft, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';

export default function AdminAppointments() {
  const { api } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchAppointments();
  }, [page, statusFilter]);

  const fetchAppointments = async () => {
    try {
      let url = `/admin/appointments?page=${page}&limit=20`;
      if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
      const response = await api.get(url);
      setAppointments(response.data.appointments);
      setTotalPages(response.data.total_pages);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pendente', className: 'bg-amber-100 text-amber-700' },
      confirmed: { label: 'Confirmada', className: 'bg-emerald-100 text-emerald-700' },
      completed: { label: 'Realizada', className: 'bg-slate-100 text-slate-700' },
      cancelled: { label: 'Cancelada', className: 'bg-red-100 text-red-700' },
      no_show: { label: 'Não compareceu', className: 'bg-red-100 text-red-700' },
    };
    const { label, className } = statusMap[status] || statusMap.pending;
    return <Badge className={className}>{label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-appointments">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Consultas</h1>
            <p className="text-slate-600 mt-1">Todas as consultas da plataforma</p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Todos os status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="confirmed">Confirmadas</SelectItem>
              <SelectItem value="completed">Realizadas</SelectItem>
              <SelectItem value="cancelled">Canceladas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Paciente</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Médico</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Data</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Pagamento</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                    Carregando...
                  </td>
                </tr>
              ) : appointments.length > 0 ? (
                appointments.map((appointment) => (
                  <tr key={appointment.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{appointment.patient_name || '-'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-slate-600">{appointment.doctor_name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-slate-600">{appointment.date} às {appointment.time_slot}</p>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="secondary">
                        {appointment.payment_type === 'health_plan' ? 'Convênio' : 'Particular'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(appointment.status)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                    Nenhuma consulta encontrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              Anterior
            </Button>
            <span className="flex items-center px-4 text-sm text-slate-600">
              Página {page} de {totalPages}
            </span>
            <Button variant="outline" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
              Próxima
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
